import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, } from 'react';
import { v4 } from 'uuid';
import { Box, Button } from '@mui/material';
import { useStore } from '@mcn-platform/models';
import { createAlignPlugin, createAutoformatPlugin, createBlockquotePlugin, createBoldPlugin, createCodeBlockPlugin, createCodePlugin, createComboboxPlugin, createDeserializeCsvPlugin, createDeserializeDocxPlugin, createDeserializeMdPlugin, 
// createDndPlugin,
createExitBreakPlugin, createFontBackgroundColorPlugin, createFontColorPlugin, createFontFamilyPlugin, createFontSizePlugin, createHeadingPlugin, createHighlightPlugin, createHorizontalRulePlugin, createImagePlugin, createIndentPlugin, createItalicPlugin, createKbdPlugin, createLinkPlugin, createListPlugin, createMediaEmbedPlugin, 
// createMentionPlugin,
createNodeIdPlugin, createNormalizeTypesPlugin, createParagraphPlugin, createPlateEditor, createPlateUI, createResetNodePlugin, createSelectOnBackspacePlugin, createSoftBreakPlugin, createStrikethroughPlugin, createSubscriptPlugin, createSuperscriptPlugin, createTablePlugin, createTodoListPlugin, createTrailingBlockPlugin, createUnderlinePlugin, deserializeMd, Plate, PlateProvider, } from '@udecode/plate';
import { createJuicePlugin } from '@udecode/plate-juice';
import { getContentsFromS3 } from '@mcn-platform/models/dist/S3Client';
import { editableProps, readOnlyProps } from './config/common/editableProps';
import { alignPlugin } from './config/align/alignPlugin';
import { autoformatPlugin } from './config/autoformat/autoformatPlugin';
import exitBreakPlugin from './config/exit-break/exitBreakPlugin';
import indentPlugin from './config/indent/indentPlugin';
import linkPlugin from './config/link/linkPlugin';
import resetBlockTypePlugin from './config/reset-node/resetBlockTypePlugin';
import selectOnBackspacePlugin from './config/select-on-backspace/selectOnBackspacePlugin';
import softBreakPlugin from './config/soft-break/softBreakPlugin';
import Toolbar from './toolbar/Toolbar';
import trailingBlockPlugin from './config/trailing-block/trailingBlockPlugin';
import { createMyPlugins, } from './config/typescript/plateTypes';
import ToolbarButtons from './config/ToolbarButtons';
import { createLastSelectionPlugin } from './config/lastSelectionPlugin';
const components = createPlateUI({});
export const basePlugins = createMyPlugins([
    createParagraphPlugin(),
    createBlockquotePlugin(),
    createTodoListPlugin(),
    createHeadingPlugin(),
    createImagePlugin(),
    createHorizontalRulePlugin(),
    createLinkPlugin(linkPlugin),
    createListPlugin(),
    createTablePlugin(),
    createMediaEmbedPlugin(),
    // createExcalidrawPlugin() as MyPlatePlugin,
    createCodeBlockPlugin(),
    createAlignPlugin(alignPlugin),
    createBoldPlugin(),
    createCodePlugin(),
    createLastSelectionPlugin(),
    createItalicPlugin(),
    createHighlightPlugin(),
    createUnderlinePlugin(),
    createStrikethroughPlugin(),
    createSubscriptPlugin(),
    createSuperscriptPlugin(),
    createFontColorPlugin(),
    createFontBackgroundColorPlugin(),
    createFontFamilyPlugin(),
    createFontSizePlugin(),
    createKbdPlugin(),
    createNodeIdPlugin(),
    // createBlockSelectionPlugin(),
    // createDndPlugin(),
    // dragOverCursorPlugin,
    createIndentPlugin(indentPlugin),
    createAutoformatPlugin(autoformatPlugin),
    createResetNodePlugin(resetBlockTypePlugin),
    createSoftBreakPlugin(softBreakPlugin),
    createExitBreakPlugin(exitBreakPlugin),
    createNormalizeTypesPlugin(),
    createTrailingBlockPlugin(trailingBlockPlugin),
    createSelectOnBackspacePlugin(selectOnBackspacePlugin),
    createComboboxPlugin(),
    // createMentionPlugin(),
    // @ts-expect-error incompatible types
    createDeserializeMdPlugin(),
    createDeserializeCsvPlugin(),
    createDeserializeDocxPlugin(),
    createJuicePlugin(),
], {
    components,
});
const getSxProp = (store) => {
    const sxprop = {};
    if (store.currentPortal?.stylesheet?.style?.sections) {
        const i = store.currentPortal?.stylesheet?.style?.sections.findIndex((s) => s.id === 'site');
        if (store.currentPortal?.stylesheet?.style?.sections[i].properties) {
            const f = store.currentPortal?.stylesheet?.style?.sections[i].properties.findIndex((p) => p.id === 'font');
            if (f > -1) {
                if (store.currentPortal?.stylesheet?.style?.sections[i].properties[f]?.value?.fontFamily) {
                    // eslint-disable-next-line max-len
                    sxprop.fontFamily = store.currentPortal?.stylesheet?.style?.sections[i].properties[f]?.value?.fontFamily;
                }
            }
        }
    }
    return sxprop;
};
const ShowMorePlate = ({ id, editableProps: showMoreEditableProps, showMore, value, }) => {
    const [expanded, setExpanded] = useState(false);
    const editorRef = useRef(null);
    const handleToggle = () => {
        setExpanded(!expanded);
    };
    const truncateText = (nodes, maxLength) => {
        let currentLength = 0;
        return Array.isArray(nodes) ? nodes.reduce((acc, node) => {
            const children = Array.isArray(node.children) ? node.children.reduce((childAcc, child) => {
                const textLength = child.text?.length || 0;
                if (currentLength + textLength > maxLength) {
                    const remainingLength = maxLength - currentLength;
                    if (remainingLength > 0) {
                        childAcc.push({
                            ...child,
                            text: child.text.slice(0, remainingLength),
                        });
                    }
                    currentLength = maxLength;
                    return childAcc;
                }
                currentLength += textLength;
                childAcc.push(child);
                return childAcc;
            }, []) : [];
            if (children.length > 0) {
                acc.push({
                    ...node,
                    children,
                });
            }
            return acc;
        }, []) : [];
    };
    const textLength = Array.isArray(value)
        ? value.reduce((acc, node) => acc + (Array.isArray(node.children)
            ? node.children.reduce((childAcc, child) => childAcc + (child.text?.length || 0), 0)
            : 0), 0) : 0;
    const nodesToShow = expanded ? value : truncateText(value, showMore);
    // Effect to reset the editor when value changes
    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.reset();
        }
    }, [expanded]);
    return (_jsxs(Box, { children: [_jsx(PlateProvider, { id: id, value: nodesToShow, plugins: basePlugins, editorRef: editorRef, children: _jsx(Plate, { id: id, editableProps: showMoreEditableProps }) }), textLength > showMore && (_jsx(Button, { disableRipple: true, variant: "text", size: "small", className: "showMoreLinkColor", onClick: handleToggle, sx: {
                    ml: 0.5,
                    p: 0,
                    typography: 'body2',
                    fontWeight: (theme) => theme.typography.fontWeightBold,
                    color: (t) => t.portal?.courseDisplay?.showMoreLinkColor?.color,
                    textTransform: 'none',
                    '&:hover': {
                        background: 'none',
                        textDecoration: 'underline',
                    },
                }, children: expanded ? 'Show less' : 'Show more' }))] }));
};
const WysiwygEditor = ({ id, value, setValue, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
name, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
error, }) => {
    const store = useStore();
    const containerRef = useRef(null);
    const [resolvedValue, setResolvedValue] = useState(null);
    useEffect(() => {
        const resolveValue = async () => {
            if (value?.s3) {
                const fetchedContent = await getContentsFromS3(value.s3.bucket, value.s3.key);
                const richtextData = JSON.parse(fetchedContent);
                setResolvedValue(richtextData.slate);
                // console.log('rich', fetchedFile);
            }
            else {
                setResolvedValue(value);
            }
        };
        try {
            resolveValue();
        }
        catch (e) {
            console.error('Error resolving value:', e);
        }
    }, [value]);
    if (resolvedValue === null) {
        return null;
    }
    return (_jsxs(PlateProvider, { id: id, initialValue: resolvedValue, 
        // value={value}
        plugins: basePlugins, onChange: setValue, children: [_jsx(Toolbar, { children: _jsx(ToolbarButtons, {}) }), _jsx(Box, { ref: containerRef, sx: {
                    '& [data-slate-editor]': {
                        padding: 0,
                    },
                    minHeight: 100,
                    ...getSxProp(store),
                }, children: _jsx(Plate, { id: id, editableProps: editableProps }) })] }));
};
WysiwygEditor.defaultProps = {
    name: '',
    error: false,
};
const WysiwygViewer = ({ id, value, showMore, }) => {
    const store = useStore();
    const [resolvedValue, setResolvedValue] = useState(value?.s3 ? null : value);
    useEffect(() => {
        const resolveValue = async () => {
            if (value?.s3) {
                const fetchedContent = await getContentsFromS3(value.s3.bucket, value.s3.key);
                const richtextData = JSON.parse(fetchedContent);
                setResolvedValue(richtextData.slate);
                // wait 10ms
                await new Promise((resolve) => { setTimeout(resolve, 100); });
                // this forces the plate to recalculate its height - which in turn fixes
                // bug in react-textarea-autosize
                console.log('force resize');
                window.dispatchEvent(new Event('resize'));
            }
            else {
                setResolvedValue(value);
            }
        };
        try {
            resolveValue();
        }
        catch (error) {
            console.error('Error resolving value:', error);
        }
    }, [value]);
    const plateId = `${id}-${v4()}`;
    // NOTE using null to indicate loading, undefined is an empty value that should be rendered
    if (resolvedValue === null) {
        return null;
    }
    return (_jsx(Box, { sx: {
            ...getSxProp(store),
        }, children: showMore
            ? (_jsx(ShowMorePlate, { id: plateId, editableProps: readOnlyProps, showMore: showMore, value: resolvedValue }))
            : (_jsx(PlateProvider, { id: plateId, initialValue: resolvedValue, plugins: basePlugins, children: _jsx(Plate, { id: plateId, editableProps: readOnlyProps }) })) }));
};
WysiwygViewer.defaultProps = {
    showMore: undefined,
};
// const serializer = withReact(createEditorPlugins({
//   plugins: basePlugins,
//   components,
//   options,
// }));
// eslint-disable-next-line @typescript-eslint/no-unused-vars, arrow-body-style
const serialize = (nodes) => {
    return null;
    // const newNodes = cloneDeep(nodes);
    // newNodes.forEach((node: any) => {
    //   if (node.children.length === 1 && node.children[0].text === '') {
    //     node.children[0].text = '&#xFEFF';
    //   }
    // });
    // // eslint-disable-next-line no-param-reassign
    // nodes = newNodes;
    // return serializeHTMLFromNodes(serializer, {
    //   plugins: basePlugins, // Plugins('serializer'),
    //   nodes,
    //   stripWhitespace: false,
    // });
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const deserialize = (html) => ({}
// deserializeHTMLToDocumentFragment(serializer, {
//   plugins: basePlugins,
//   element: html,
//   stripWhitespace: false,
// })
);
const deserializeMarkdown = (md) => {
    const editor = createPlateEditor({ plugins: basePlugins });
    return deserializeMd(editor, md);
};
export default WysiwygEditor;
export { WysiwygViewer, serialize, deserializeMarkdown, };
//# sourceMappingURL=WysiwygEditor.js.map